import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList } from "lucide-react";
import { format } from "date-fns";
import { usePendingSurveys, type PendingSurveyAssignment } from "@/hooks/usePendingSurveys";

const ORANGE = "#C94F13";
const BLUE = "#29A4E3";
const FONT_STACK = "'Public Sans', Arial, sans-serif";

function isOverdue(dueAt: string | null): boolean {
  if (!dueAt) return false;
  return new Date(dueAt).getTime() < Date.now();
}

function AssignmentRow({ a }: { a: PendingSurveyAssignment }) {
  const navigate = useNavigate();
  const overdue = isOverdue(a.dueAt);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border bg-background/50 p-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold truncate">{a.title}</span>
          <Badge
            className="text-white border-transparent"
            style={{
              backgroundColor: a.surveyType === "impact" ? ORANGE : BLUE,
            }}
          >
            {a.surveyType === "impact" ? "Impact" : "General"}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {a.waveLabel && (
            <span className="text-muted-foreground">Wave: {a.waveLabel}</span>
          )}
          {a.dueAt && (
            <span
              className={overdue ? "font-medium text-destructive" : "text-muted-foreground"}
            >
              Due {format(new Date(a.dueAt), "PPP")}
              {overdue && " (overdue)"}
            </span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => navigate(`/survey/${a.assignmentId}`)}
        style={{ backgroundColor: ORANGE, color: "white" }}
        className="shrink-0 hover:opacity-90"
      >
        Start Survey
      </Button>
    </div>
  );
}

export function PendingSurveysCard() {
  const { pendingAssignments, isLoading } = usePendingSurveys();
  const [expanded, setExpanded] = useState(false);

  const hasOverdue = useMemo(
    () => pendingAssignments.some((a) => isOverdue(a.dueAt)),
    [pendingAssignments],
  );

  if (isLoading) {
    return (
      <Card style={{ fontFamily: FONT_STACK, borderColor: `${ORANGE}40` }}>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48 mt-2" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!pendingAssignments || pendingAssignments.length === 0) {
    return null;
  }

  const total = pendingAssignments.length;
  const visible = expanded ? pendingAssignments : pendingAssignments.slice(0, 3);
  const hiddenCount = total - visible.length;

  return (
    <Card
      style={{
        fontFamily: FONT_STACK,
        borderColor: `${ORANGE}66`,
        backgroundColor: `${ORANGE}0D`,
      }}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardList className="h-5 w-5" style={{ color: ORANGE }} />
          Surveys Awaiting Your Response
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {total === 1
            ? "You have a survey to complete."
            : `You have ${total} surveys to complete.`}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {visible.map((a) => (
          <AssignmentRow key={a.assignmentId} a={a} />
        ))}
        {!expanded && hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            View all {total} surveys
          </button>
        )}
        {expanded && total > 3 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Show fewer
          </button>
        )}
      </CardContent>
      {hasOverdue && (
        <CardFooter>
          <p className="text-xs text-muted-foreground">
            One or more surveys are past their due date. Please complete them when you can.
          </p>
        </CardFooter>
      )}
    </Card>
  );
}

export default PendingSurveysCard;
